import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';

interface FieldExtractionDialogProps {
  open: boolean;
  onClose: () => void;
  extractingFields: boolean;
  extractedFields: string[];
  templateId?: string;
  showSnackbar: (message: string, severity?: 'success' | 'error') => void;
}

const FieldExtractionDialog: React.FC<FieldExtractionDialogProps> = ({
  open,
  onClose,
  extractingFields,
  extractedFields,
  templateId,
  showSnackbar,
}) => {
  const generateJsonExample = () => {
    if (!templateId || extractedFields.length === 0) return '';

    const fieldMappings: Record<string, string> = {};
    extractedFields.forEach(field => {
      fieldMappings[field] = "string";
    });

    const jsonExample = {
      templateId: templateId,
      fieldMappings,
      filename: "string"
    };

    return JSON.stringify(jsonExample, null, 2);
  };

  const handleCopyJson = async () => {
    const jsonText = generateJsonExample();
    if (jsonText) {
      try {
        await navigator.clipboard.writeText(jsonText);
        showSnackbar('JSON example copied to clipboard');
      } catch (err) {
        console.error('Failed to copy JSON:', err);
        showSnackbar('Failed to copy JSON example', 'error');
      }
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Extracted Fields</DialogTitle>
      <DialogContent>
        {extractingFields ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {extractedFields.length > 0 ? (
              <>
                <Typography variant="body2" paragraph>
                  The following fields were extracted from the document. You can use these fields in your templates.
                </Typography>
                <List>
                  {extractedFields.map((field, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => {
                            navigator.clipboard.writeText(field);
                            showSnackbar('Field name copied to clipboard');
                          }}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={field} />
                    </ListItem>
                  ))}
                </List>
                
                {templateId && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h6">
                          JSON API Example
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={handleCopyJson}
                        >
                          Copy JSON
                        </Button>
                      </Box>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Use this JSON structure to call the API with the extracted fields:
                      </Typography>
                      <Paper 
                        sx={{ 
                          p: 2, 
                          backgroundColor: 'grey.50',
                          border: '1px solid',
                          borderColor: 'grey.200'
                        }}
                      >
                        <Typography 
                          component="pre" 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-wrap',
                            margin: 0,
                            fontSize: '0.875rem'
                          }}
                        >
                          {generateJsonExample()}
                        </Typography>
                      </Paper>
                    </Box>
                  </>
                )}
              </>
            ) : (
              <Alert severity="info">
                No mappable fields were found in this document. Fields should be in the format {'{{'} FieldName {'}}'}  within the PDF.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FieldExtractionDialog;
